import { useMemo } from 'react';

import { useFetchData } from '../hooks/useFetchData';
import { CollectionWithCreator, ScopePagePair, SummarizationFile } from '../Types/dealGPT';
import { ChatMode } from '../Types/enums';
import { collectionsPathUrl, summaryChatFilesPathUrl } from '../util/routesUrls';

interface UseSummaryFilesParams {
  projectId?: string;
  summaryFileIds?: string[];
}

export const useSummaryFiles = ({ projectId, summaryFileIds }: UseSummaryFilesParams) => {
  const shouldFetchSummary = Boolean(projectId && summaryFileIds && summaryFileIds.length > 0);

  const summaryFilesUrl = useMemo(() => {
    if (!projectId || !summaryFileIds || summaryFileIds.length === 0) return '';
    return summaryChatFilesPathUrl(projectId, summaryFileIds);
  }, [projectId, summaryFileIds]);

  const { data: rawSummaryFiles } = useFetchData<SummarizationFile[]>({
    urlPath: summaryFilesUrl,
    skip: !shouldFetchSummary,
  });

  const summaryFiles: SummarizationFile[] = useMemo(() => {
    if (!shouldFetchSummary) return [];
    return rawSummaryFiles ?? [];
  }, [shouldFetchSummary, rawSummaryFiles]);

  const summaryCollectionIds = useMemo(() => {
    if (!summaryFiles) return [];
    return summaryFiles.map((sf) => sf.collectionId).filter((cid): cid is string => Boolean(cid));
  }, [summaryFiles]);

  const { data: summaryCollections } = useFetchData<CollectionWithCreator[]>({
    urlPath: collectionsPathUrl({ ids: summaryCollectionIds }),
    skip: summaryCollectionIds.length === 0,
  });

  const activeTab = useMemo(() => {
    if (!summaryFiles || summaryFiles.length === 0) return ChatMode.ANALYSIS;
    return summaryFiles[0].collectionId ? ChatMode.DISCOVERY : ChatMode.ANALYSIS;
  }, [summaryFiles]);

  const filteredSummaryFiles = useMemo(() => {
    if (!summaryFiles) return [];

    return activeTab === ChatMode.DISCOVERY
      ? summaryFiles.filter((f) => f.collectionId)
      : summaryFiles.filter((f) => f.pageName);
  }, [summaryFiles, activeTab]);

  const totalDocs = useMemo(() => {
    return filteredSummaryFiles.reduce((acc, file) => acc + 1 + (file.references?.length ?? 0), 0);
  }, [filteredSummaryFiles]);

  const scopePagePairs: ScopePagePair[] = useMemo(() => {
    const pairs: ScopePagePair[] = [];

    filteredSummaryFiles.forEach((file) => {
      if (file.scope && file.pageName) {
        pairs.push({ scope: file.scope, pageName: file.pageName });
      }
    });

    const uniquePairs = Array.from(
      new Map(pairs.map((p) => [`${p.scope}|${p.pageName}`, p])).values()
    );

    return uniquePairs;
  }, [filteredSummaryFiles]);

  return {
    summaryFiles,
    filteredSummaryFiles,
    summaryCollections,
    activeTab,
    totalDocs,
    scopePagePairs,
  };
};
